import { config } from 'config';
import { getStatus } from 'shared/fixtures';
import i18n from 'i18nextInstance';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import { parseDownloadTurns } from 'core/Turns';
import statisticsServices from './services';
import {
    all,
    call,
    put,
    select,
    takeLatest
} from 'redux-saga/effects';
import {
    fixedPercentage,
    formatDate,
    identificationFormat,
    minutesFormat,
    objectToArray,
    parseAbsenceReportStatus,
    parseBool,
    parseDiagnosis,
    parseDiagnosisCode,
    parseDiagnosticStatus,
    parseDiagnosticType,
    parseGender,
    parseLicenseNextStep,
    parsePreworkType,
    parseSelect,
    parseUserType,
    uppercaseFirst
} from 'shared/utils';
import {
    parseCallType,
    parseLicenseAttentionType,
    parsePaymentMethod,
    parsePsychosocialRiskAnswer,
    parseStatisticsAbsencesByLicenseMonthly,
    statisticsActions
} from './';
const { externalService } = config;

function* _getStatisticsAbsenceRate ({ payload: { company_id, month, year } }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const params = {
            company_id: company_id ?? null,
            month,
            year
        };
        const monthlyParams = {
            company_id: company_id ?? null,
            end: `${ year }-${ month.toString().padStart(2, '0') }`,
            start: `${ year - 1 }-${ month.toString().padStart(2, '0') }`
        };
        const [{ data }, { data: monthlyData }] = yield all([
            call(statisticsServices.fetchAbsenceRateData, null, params),
            call(statisticsServices.fetchAbsenceRateDataMonthly, null, monthlyParams)]
        );
        yield put(statisticsActions.setStatisticsAbsenceRate(orderBy(data, ['percentage', 'name'], ['desc', 'asc'])));
        const years = [];
        const parsedData = monthlyData.map(elem => {
            const { absence_rate, month, year } = elem;
            if (!years.includes(year)) years.push(year);
            const keyName = `absence_rate_${ year }`;
            return {
                ...elem,
                key: month,
                [keyName]: absence_rate,
                label: moment().set('day', 1).set('month', month - 1).format('MMM')
            };
        });
        yield put(statisticsActions.setStatisticsAbsenceRateMonthly({
            data: parsedData,
            years
        }));
    } catch (e) {
        yield put(statisticsActions.setStatisticsAbsenceRate([]));
        yield put(statisticsActions.setStatisticsAbsenceRateMonthly([]));
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsAbsenceReports ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchAbsenceReportsData, null, payload);
        yield put(statisticsActions.setStatisticsAbsenceReports(data));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsAbsenceReportsDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsAbsenceReportsDownloading(true));
        const { data: { data } } = yield call(statisticsServices.fetchAbsenceReportsPage, null, { ...payload, one_page: 1 });
        yield put(statisticsActions.setStatisticsAbsenceReportsDownload(data));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsAbsenceReportsDownloading(false));
    }
}

function* _getStatisticsAbsenceReportsPage ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsAbsenceReportsPageLoading(true));
        const { data } = yield call(statisticsServices.fetchAbsenceReportsPage, null, payload);
        yield put(statisticsActions.setStatisticsAbsenceReportsPage({ filters: payload, ...data }));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsAbsenceReportsPageLoading(false));
    }
}

function* _getStatisticsAbsencesActive ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data: { data } } = yield call(statisticsServices.fetchAbsencesActiveData, null, payload);
        const parseStatisticsAbsencesActive = list => list.map(absence => {
            const {
                actual,
                from_date,
                status,
                to_date
            } = absence;
            const diff =  moment.min([moment(), moment(to_date, 'YYYY-MM-DD')]).diff(moment(from_date, 'YYYY-MM-DD'), 'days');
            const current_days = actual ? diff + 1 : 0;
            return ({
                ...absence,
                current_days,
                from_date: formatDate(from_date),
                status: parseAbsenceReportStatus(status),
                to_date: formatDate(to_date)
            });
        });
        yield put(statisticsActions.setStatisticsAbsencesActive(parseStatisticsAbsencesActive(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsAbsencesByLicense ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));

        const { config: { configurations: { licenses } } } = yield select();
        const [{ data }, { data: monthlyData }] = yield all([
            call(statisticsServices.fetchAbsencesByLicenseData, null, payload),
            call(statisticsServices.fetchAbsencesByLicenseMonthlyData, null, payload)]
        );

        const absencesByLicense = [{
            key: 'total_days',
            label: i18n.t('Días totales'),
            value: (data['total_days'] ?? 0).toString()
        }, ...orderBy(licenses.filter(({ effective_license }) => !!effective_license).map(({ key, label }) => ({
            days: !!data[key] ? data[key]['days'] : 0,
            key,
            label,
            percentage: !!data[key] ? data[key]['percentage'] : 0,
            value: !!data[key] ? `${ data[key]['days'] } (${ data[key]['percentage'] }%)` : '-'
        })), ['percentage', 'label'], ['desc', 'asc'])];
        yield put(statisticsActions.setStatisticsAbsencesByLicense(absencesByLicense));
        yield put(statisticsActions.setStatisticsAbsencesByLicenseMonthly(parseStatisticsAbsencesByLicenseMonthly(monthlyData)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsAbsencesByLicenseMonthly ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsUpdating(true));
        const { data } = yield call(statisticsServices.fetchAbsencesByLicenseMonthlyData, null, payload);
        yield put(statisticsActions.setStatisticsAbsencesByLicenseMonthly(parseStatisticsAbsencesByLicenseMonthly(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsUpdating(false));
    }
}

function* _getStatisticsDiagnosis ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchDiagnosisData, null, payload);
        const parsedData = orderBy(data, ['percentage', 'name'], ['desc', 'asc']).map(item => {
            const { confirmed, percentage, presumptives } = item;
            return {
                ...item,
                confirmed: fixedPercentage(confirmed),
                percentage: fixedPercentage(percentage),
                presumptives: fixedPercentage(presumptives)
            };
        });
        yield put(statisticsActions.setStatisticsDiagnosis(parsedData));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsDiagnosisGrouped ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchDiagnosisGroupedData, null, payload);
        const parsedData = orderBy(data, ['percentage', 'name'], ['desc', 'asc']).map(item => {
            const { confirmed, percentage, presumptives } = item;
            return {
                ...item,
                confirmed: fixedPercentage(confirmed),
                percentage: fixedPercentage(percentage),
                presumptives: fixedPercentage(presumptives)
            };
        });
        yield put(statisticsActions.setStatisticsDiagnosisGrouped(parsedData));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsGeneral ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const data = yield call(statisticsServices.fetchGeneralData, null, payload);
        yield put(statisticsActions.setStatisticsGeneral(data));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsHealthSurveys ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchHealthSurveys, null, payload);
        yield put(statisticsActions.setStatisticsHealthSurveys(data));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsHealthSurveysDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsHealthSurveysDownloading(true));
        const { data } = yield call(statisticsServices.fetchHealthSurveysDownload, null, payload);
        const parseAnswers = answers => answers.reduce((acc, answer) => {
            acc[answer.survey_question_uuid] = answer.answer ?? '';
            return acc;
        }, {});
        const parsedDownloadData = data.map(hs => ({
            ...hs,
            ...parseAnswers(hs.answers),
            created_at: moment(hs.created_at).format('DD/MM/YYYY HH:mm'),
            obesity_risk: parseBool(hs.obesity_risk),
            patient_full_name: `${ hs.patient_last_name }, ${ hs.patient_first_name }`
        }));
        yield put(statisticsActions.setStatisticsHealthSurveysDownload(parsedDownloadData));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsHealthSurveysDownloading(false));
    }
}

function* _getStatisticsLicenses ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const {
            data: {
                effectiveness: percentages,
                ...summary
            }
        } = yield call(statisticsServices.fetchLicensesSummary, null, payload);
        const { data: { data } } = yield call(statisticsServices.fetchLicensesData, null, payload);
        const { config: { configurations: { licenses } } } = yield select();

        const effectiveness = {
            effective: orderBy(licenses.filter(({ effective_license }) => !!effective_license).map(({ key, label }) => ({
                key,
                label,
                value: percentages[key] ?? 0
            })), 'label'),
            global: [{
                key: 'total_effective',
                label: i18n.t('Efectivas'),
                value: percentages['total'] ?? 0
            }, {
                key: 'total_not_effective',
                label: i18n.t('No efectivas'),
                value: (100 - percentages['total']) ?? 0
            }],
            not_effective: orderBy(licenses.filter(({ effective_license }) => !effective_license).map(({ key, label }) => ({
                key,
                label,
                value: percentages[key] ?? 0
            })), 'label')
        };
        const parseStatisticsLicenses = list => list.map(({
            reasons,
            ...item
        }) => ({
            ...item,
            ...reasons
        }));
        yield put(statisticsActions.setStatisticsLicenses(parseStatisticsLicenses(data)));
        yield put(statisticsActions.setStatisticsLicensesSummary({ effectiveness, ...summary }));
    } catch (e) {
 
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsLicensesActive ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchLicensesDownload, null, payload);
        const parseStatisticsLicensesActive = list => list.map(license => {
            const {
                license_from_date,
                license_next_step,
                license_next_step_date,
                license_to_date,
                patient_gender
            } = license;
            const diff =  moment.min([moment(), moment(license_to_date, 'YYYY-MM-DD')]).diff(moment(license_from_date, 'YYYY-MM-DD'), 'days');
            const currentLicense = moment().isBetween(moment(license_from_date), moment(license_to_date), 'day', '[]');
            const currentDays = currentLicense ? diff + 1 : 0;
            return ({
                ...license,
                current: currentLicense,
                current_days: currentDays,
                discharge_date: license_next_step === 'discharged' ? formatDate(license_next_step_date) : '',
                license_from_date: formatDate(license_from_date),
                license_to_date: formatDate(license_to_date),
                new_control: license_next_step === 'new_control' ? formatDate(license_next_step_date) : '',
                patient_gender: parseGender(patient_gender)
            });
        });
        yield put(statisticsActions.setStatisticsLicensesActive(parseStatisticsLicensesActive(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsLicensesDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLicensesDownloading(true));
        const { data } = yield call(statisticsServices.fetchLicensesDownload, null, payload);
        const parseStatisticsLicensesDownload = list => !list ? [] : list.map(({
            consultation_reasons,
            diagnoses,
            diagnostic_date,
            diagnostic_status,
            license_attention_type,
            license_external_request,
            license_from_date,
            license_next_step,
            license_next_step_date,
            license_requested_from_date,
            license_requested_to_date,
            license_requested_days,
            license_to_date,
            patient_gender,
            ...rest
        }) => ({
            ...rest,
            consultation_reasons: !isEmpty(consultation_reasons) ? parseDiagnosis(consultation_reasons) : '',
            diagnoses: !isEmpty(diagnoses) ? parseDiagnosis(diagnoses) : '',
            diagnosis_code: !isEmpty(diagnoses) ? parseDiagnosisCode(diagnoses) : '',
            diagnostic_date_date: !!diagnostic_date ? moment(diagnostic_date).format('DD/MM/YYYY') : '',
            diagnostic_date_time: !!diagnostic_date ? moment(diagnostic_date).format('HH:mm') : '',
            diagnostic_status: parseDiagnosticStatus(diagnostic_status) ?? '',
            license_attention_type: parseLicenseAttentionType(license_attention_type),
            license_external_request: parseBool(license_external_request),
            license_from_date: !!license_from_date ? moment(license_from_date).format('DD/MM/YYYY') : '',
            license_next_step: parseLicenseNextStep(license_next_step),
            license_next_step_date: license_next_step_date ? moment(license_next_step_date).format('DD/MM/YYYY') : '',
            license_requested_days: license_external_request ? (license_requested_days ?? 0) : '',
            license_requested_from_date: !!license_requested_from_date ? moment(license_requested_from_date).format('DD/MM/YYYY') : '',
            license_requested_to_date: !!license_requested_to_date ? moment(license_requested_to_date).format('DD/MM/YYYY') : '',
            license_to_date: license_to_date ? moment(license_to_date).format('DD/MM/YYYY') : '',
            patient_gender: parseGender(patient_gender)
        }));
        yield put(statisticsActions.setStatisticsLicensesDownload(parseStatisticsLicensesDownload(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLicensesDownloading(false));
    }
}

function* _getStatisticsPatientsAttended ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchPatientAttentionsData, null, payload);
        const parseStatisticsPatientsAttended = data => {
            const parsePatientsAttendedData = data => data.map(elem => {
                const { id, name, total } = elem;
                return ({
                    ...elem,
                    amount: total,
                    key: id?.toString(),
                    label: name
                });
            });
            const {
                top_diagnostics,
                top_license_reasons,
                top_positions
            } = data;
            return {
                ...data,
                top_diagnostics: parsePatientsAttendedData(top_diagnostics),
                top_license_reasons: parsePatientsAttendedData(top_license_reasons),
                top_positions: parsePatientsAttendedData(top_positions)
            };
        };
        yield put(statisticsActions.setStatisticsPatientsAttended(parseStatisticsPatientsAttended(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsPatientsAttendedDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsPatientsAttendedDownloading(true));
        const { data } = yield call(statisticsServices.fetchPatientAttentionsDownload, null, payload);
        const parseStatisticsPatientsAttendedDownload = list => list.map(item => ({
            ...item,
            diagnoses: (item?.diagnoses ?? []).sort().join(', '),
            license_reasons: (item?.license_reasons ?? []).sort().join(', '),
            patient_gender: parseGender(item.patient_gender)
        }));
        yield put(statisticsActions.setStatisticsPatientsAttendedDownload(parseStatisticsPatientsAttendedDownload(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsPatientsAttendedDownloading(false));
    }
}

function* _getStatisticsLicensesGranted ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { config: { configurations: { licenses } } } = yield select();
        const { data } = yield call(statisticsServices.fetchLicensesGrantedData, null, payload);
        const licensesGranted = [{
            key: 'total_days',
            label: 'Días totales',
            value: (data['total_days'] ?? 0).toString()
        }, ...orderBy(licenses.filter(({ effective_license }) => !!effective_license).map(({ key, label }) => ({
            days: !!data[key] ? data[key]['days'] : 0,
            key,
            label,
            percentage: !!data[key] ? data[key]['percentage'] : 0,
            value: !!data[key] ? `${ data[key]['days'] } (${ data[key]['percentage'] }%)` : '-'
        })), ['percentage', 'label'], ['desc', 'asc'])];
        yield put(statisticsActions.setStatisticsLicensesGranted(licensesGranted));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsLicensesHealthIndex ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchLicensesHealthIndexData, null, payload);
        yield put(statisticsActions.setStatisticsLicensesHealthIndex(data));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsPreworks ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchPreworksData, null, payload);
        yield put(statisticsActions.setStatisticsPreworks(data));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsPreworksDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsPreworksDownloading(true));
        const { data } = yield call(statisticsServices.fetchPreworksDownloadData, null, payload);
        const parseStatisticsPreworksDownload = list => list.map(item => {
            const { created_at, exams, finalized_at, patient, sent_at, type } = item;
            return ({
                ...item,
                created_at: moment(created_at).format('DD/MM/YYYY'),
                exams: exams.filter(({ checked }) => !!checked).map(({ name }) => name).join(', '),
                finalized_at: finalized_at ? moment(finalized_at).format('DD/MM/YYYY') : '',
                patient_first_name: patient?.first_name,
                patient_identification: patient?.identification,
                patient_last_name: patient?.last_name,
                sent_at: sent_at ? moment(sent_at).format('DD/MM/YYYY') : '',
                type: parsePreworkType(type)
            });
        });
        yield put(statisticsActions.setStatisticsPreworksDownload(parseStatisticsPreworksDownload(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsPreworksDownloading(false));
    }
}

function* _getStatisticsProfessionals ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchProfessionalsData, null, payload);
        const parseStatisticsProfessionals = list => !list ? [] : orderBy(list.map(({
            attended,
            average,
            call_quantity,
            duration,
            evolutions,
            first_name,
            last_name,
            patient_turnaround_time_avg,
            patient_waiting_duration_avg,
            received,
            rejected,
            successful,
            unattended,
            ...professional
        }) => ({
            ...professional,
            attended: attended ?? 0,
            average: minutesFormat(average),
            evolutions: evolutions ?? 0,
            fullName: `${ last_name }, ${ first_name }`,
            patient_turnaround_time_avg: minutesFormat(patient_turnaround_time_avg, 'colon'),
            patient_waiting_duration_avg: minutesFormat(patient_waiting_duration_avg, 'colon'),
            received: call_quantity ?? 0,
            rejected: rejected ?? 0,
            successful: successful ?? 0,
            total: minutesFormat(duration),
            unattended: unattended ?? 0
        })), ['fullName'], ['asc']);
        yield put(statisticsActions.setStatisticsProfessionals(parseStatisticsProfessionals(data)));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsProfessionalsDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsProfessionalsDownloading(true));
        const { data } = yield call(statisticsServices.fetchProfessionalsDownload, null, payload);
        const parseStatisticsProfessionalsDownload = list => list.map(item => {
            const {
                admission_accepted_duration,
                admission_assigned_duration,
                admission_patient_admitted_at,
                appointment_type,
                call_id,
                call_type,
                caller_first_name,
                caller_identification,
                caller_last_name,
                computed_time,
                consultation_reasons,
                coverage_validation,
                created_at,
                diagnoses,
                diagnostic_identifier,
                diagnostic_status,
                diagnostic_type,
                doctor_call,
                doctor_first_name,
                doctor_identification,
                doctor_last_name,
                doctor_specialties,
                duration,
                external,
                external_service,
                finalized_at,
                inroom_at,
                note,
                participants,
                patient_affiliate,
                patient_coverage,
                patient_birth_date,
                patient_city,
                patient_district,
                patient_email,
                patient_first_name,
                patient_identification,
                patient_last_name,
                patient_province,
                patient_turnaround_time,
                patient_waiting_duration,
                payment_method,
                quality,
                recommendations,
                session,
                specialty,
                status,
                statusDetail,
                symptoms = [],
                type
            } = item;

            const parseCoverageValidation = coverageValidation => {
                if (!coverageValidation) return '';
                const { affiliate_number = '', created_at, medical_coverage_name = '', plan_name = '' } = coverage_validation;
                return `${ moment(created_at).format('DD/MM/YYYY HH:mm') } - ${ medical_coverage_name } - ${ plan_name } - ${ affiliate_number }`;
            };

            return ({
                admission_accepted_duration: minutesFormat(admission_accepted_duration ?? 0, 'colon'),
                admission_assigned_duration: minutesFormat(admission_assigned_duration ?? 0, 'colon'),
                admission_patient_admitted_at: admission_patient_admitted_at ? moment(admission_patient_admitted_at, 'YYYY-MM-DD HH:mm').format('DD/MM/YYYY HH:mm') : '',
                appointment_type: !appointment_type ? '' : uppercaseFirst(appointment_type),
                call_id,
                call_type: parseCallType(call_type),
                caller_first_name: !!caller_first_name ? caller_first_name : patient_first_name,
                caller_identification: identificationFormat(!!caller_identification ? caller_identification : patient_identification),
                caller_last_name: !!caller_last_name ? caller_last_name : patient_last_name,
                computed_time,
                consultation_reasons: !isEmpty(consultation_reasons) ? parseDiagnosis(consultation_reasons) : '',
                coverage_validation: parseCoverageValidation(coverage_validation),
                day: moment(created_at, 'YYYY-MM-DD HH:mm:SS').format('YYYY/MM/DD'),
                diagnoses: !isEmpty(diagnoses) ? parseDiagnosis(diagnoses) : '',
                diagnostic_identifier,
                diagnostic_status: parseDiagnosticStatus(diagnostic_status) ?? '',
                diagnostic_type: parseDiagnosticType(diagnostic_type) ?? '',
                doctor_call: doctor_call ? i18n.t('Si') : i18n.t('No'),
                doctor_first_name,
                doctor_identification: identificationFormat(doctor_identification),
                doctor_last_name,
                doctor_specialties: doctor_specialties.map(({ name }) => name).join(', '),
                doctor_type: !external || (externalService && !external_service) ? i18n.t('Externo') : i18n.t('Servicio'),
                duration: minutesFormat(duration, 'colon'),
                finalized_at: finalized_at ? moment(finalized_at).format('DD/MM/YYYY HH:mm') : '',
                inroom_at: inroom_at ? moment(inroom_at).format('DD/MM/YYYY HH:mm') : '',
                note,
                participants,
                patient_affiliate,
                patient_birth_date: patient_birth_date ? moment(patient_birth_date, 'YYYY-MM-DD').format('DD/MM/YYYY') : '',
                patient_city: patient_city ? patient_city.name : '',
                patient_coverage,
                patient_district: patient_district ? patient_district.name : '',
                patient_email,
                patient_province: patient_province ? patient_province.name : '',
                patient_turnaround_time: minutesFormat(patient_turnaround_time, 'colon'),
                patient_waiting_duration: minutesFormat(patient_waiting_duration, 'colon'),
                payment_method: parsePaymentMethod(payment_method),
                quality: quality ? parseInt(quality, 10) / 5 : '',
                recommendations: recommendations ? recommendations : '',
                session: session ? parseInt(session, 10) / 5 : '',
                specialty: specialty || '',
                status: getStatus(call_type === 'evolution' ? 'Evolution' : status, statusDetail, moment(created_at)).statusLabel,
                symptoms: isEmpty(symptoms) ? '' : symptoms.map(({ name }) => name).join(', '),
                time: moment(created_at, 'YYYY-MM-DD HH:mm:SS').format('HH:mm'),
                type: type === '1' ? i18n.t('Interconsulta') : i18n.t('Atención')
            });
        });
        yield put(statisticsActions.setStatisticsProfessionalsDownload(parseStatisticsProfessionalsDownload(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsProfessionalsDownloading(false));
    }
}

function* _getStatisticsQuizzes ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchQuizzes, null, payload);
        yield put(statisticsActions.setStatisticsQuizzes(data));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsQuizzesDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsQuizzesDownloading(true));
        const { data: { data, structure } } = yield call(statisticsServices.fetchQuizzesDownload, null, payload);
        const parseAnswers = answers => answers.reduce((acc, answer) => {
            acc[answer.survey_question_uuid] = answer.answer ?? '';
            return acc;
        }, {});
        const parsedDownloadData = data.map(qd => ({
            ...qd,
            ...parseAnswers(qd.answers),
            created_at: moment(qd.created_at).format('DD/MM/YYYY HH:mm')
        }));
        yield put(statisticsActions.setStatisticsQuizzesDownload({
            data: parsedDownloadData,
            structure
        }));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsQuizzesDownloading(false));
    }
}

function* _getStatisticsPsychosocialRisk ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchPsychosocialRisk, null, payload);
        yield put(statisticsActions.setStatisticsPsychosocialRisk(data));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsPsychosocialRiskDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsPsychosocialRiskDownloading(true));
        const { data: { data, structure } } = yield call(statisticsServices.fetchPsychosocialRiskDownload, null, payload);
        const parseAnswers = answers => answers.reduce((acc, answer) => {
            acc[answer.survey_question_uuid] = parsePsychosocialRiskAnswer(answer.answer);
            return acc;
        }, {});
        const parsedDownloadData = data.map(qd => ({
            ...qd,
            ...parseAnswers(qd.answers),
            created_at: moment(qd.created_at).format('DD/MM/YYYY HH:mm')
        }));
        yield put(statisticsActions.setStatisticsPsychosocialRiskDownload({
            data: parsedDownloadData,
            structure
        }));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsPsychosocialRiskDownloading(false));
    }
}

function* _getStatisticsRecipeRequests ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchRecipeRequests, null, payload);
        yield put(statisticsActions.setStatisticsRecipeRequests(data));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsRecipeRequestsDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsRecipeRequestsDownloading(true));
        const { data: { data } } = yield call(statisticsServices.fetchRecipeRequestsDownload, null, payload);
        const parseStatisticsRecipeRequestsDownload = list => list.map(rr => {
            const { api_error_message, api_status, created_at, doctor, patient } = rr;
            return {
                ...rr,
                api_error_message: api_error_message ?? '',
                api_status: api_status ?? '',
                created_at: moment(created_at).format('DD/MM/YYYY HH:mm'),
                doctor_identification: doctor?.identification,
                doctor_name: `${ doctor?.last_name }, ${ doctor?.first_name }`,
                patient_identification: patient?.identification,
                patient_name: `${ patient?.last_name }, ${ patient?.first_name }`
            };
        });
        yield put(statisticsActions.setStatisticsRecipeRequestsDownload(parseStatisticsRecipeRequestsDownload(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsRecipeRequestsDownloading(false));
    }
}

function* _getStatisticsRecommendations ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchRecommendationsData, null, payload);
        yield put(statisticsActions.setStatisticsRecommendations(orderBy(data, ['percentage', 'name'], ['desc', 'asc'])));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsRiskFactors ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchRiskFactorsData, null, payload);
        const parsedRiskFactors = orderBy(parseSelect(data), ['patients', 'label'], ['desc', 'asc']).map(rf => ({
            ...rf,
            key: rf?.id?.toString()
        }));
        yield put(statisticsActions.setStatisticsRiskFactors(parsedRiskFactors));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsRiskFactorsDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsRiskFactorsDownloading(true));
        const { data: { data } } = yield call(statisticsServices.fetchRiskFactorsPage, null, { ...payload, one_page: 1 });
        const parseStatisticsRiskFactorsDownload = data => data.map(elem => {
            const { company, position, workplace } = elem;
            return {
                ...elem,
                company: company?.name,
                position: position?.name,
                workplace: workplace?.name
            };
        });
        yield put(statisticsActions.setStatisticsRiskFactorsDownload(parseStatisticsRiskFactorsDownload(data)));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsRiskFactorsDownloading(false));
    }
}

function* _getStatisticsRiskFactorsPage ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsRiskFactorsPageLoading(true));
        const { data } = yield call(statisticsServices.fetchRiskFactorsPage, null, payload);
        yield put(statisticsActions.setStatisticsRiskFactorsPage({
            ...data,
            filters: payload
        }));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsRiskFactorsPageLoading(false));
    }
}

function* _getStatisticsSessions ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data: { doctors } } = yield call(statisticsServices.fetchSessionsData, null, payload);
        const parseStatisticsSessions = list => !list ? [] : orderBy(list.map(({
            acum,
            first_name,
            identification,
            last_name
        }) => ({
            first_name,
            identification,
            last_name,
            time: minutesFormat(acum)
        })), ['last_name', 'first_name'], ['asc, asc']);
        yield put(statisticsActions.setStatisticsSessions(parseStatisticsSessions(doctors)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsSessionsDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsSessionsDownloading(true));
        const { data: { sessions } } = yield call(statisticsServices.fetchSessionsData, null, payload);
        const parseStatisticsSessionsDownload = list => !list ? [] : orderBy(list.map(({
            available_since,
            first_name,
            identification,
            last_name,
            session_duration,
            unavailable_at
        }) => ({
            available_since,
            first_name,
            identification,
            last_name,
            seconds: session_duration,
            time: minutesFormat(session_duration),
            time_from: moment(available_since).format('DD/MM/YYYY HH:mm:ss'),
            time_to: moment(unavailable_at).format('DD/MM/YYYY HH:mm:ss')
        })), ['available_since'], ['desc']);
        yield put(statisticsActions.setStatisticsSessionsDownload(parseStatisticsSessionsDownload(sessions)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsSessionsDownloading(false));
    }
}

function* _getStatisticsSurveys ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchSurveysData, null, payload);
        yield put(statisticsActions.setStatisticsSurveys(data));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsSurveysDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsSurveysDownloading(true));
        const { data } = yield call(statisticsServices.fetchSurveysDownloadData, null, payload);
        const parseStatisticsSurveysDownload = list => !list ? [] : list.map(({
            audio,
            created_at,
            user_gender,
            user_type,
            video,
            ...data
        }) => ({
            audio: !!audio ? i18n.t('Si') : i18n.t('No'),
            created_at: moment(created_at).format('DD/MM/YYYY HH:mm'),
            user_gender: parseGender(user_gender),
            user_type: parseUserType(user_type),
            video: !!video ? i18n.t('Si') : i18n.t('No'),
            ...data
        }));
        yield put(statisticsActions.setStatisticsSurveysDownload(parseStatisticsSurveysDownload(data)));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsSurveysDownloading(false));
    }
}

function* _getStatisticsSymptoms ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchSymptomsData, null, payload);
        yield put(statisticsActions.setStatisticsSymptoms(orderBy(data, ['percentage', 'name'], ['desc', 'asc'])));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsTurns ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchAppointmentStatistics, null, payload);
        const parseStatisticsTurns = (payload = {}) => {
            const counters = [{
                key: 'pending',
                label: i18n.t('Pendientes')
            }, {
                key: 'absent',
                label: i18n.t('Ausentes')
            }, {
                key: 'cancelled',
                label: i18n.t('Cancelados')
            }, {
                key: 'finalized',
                label: i18n.t('Atendidos')
            }];
            const profiles = [{
                key: 'by_admin',
                label: i18n.t('Admin')
            }, {
                key: 'by_doctor',
                label: i18n.t('Profesional')
            }, {
                key: 'by_patient',
                label: i18n.t('Paciente')
            }];
            const addLabelToArray = (array, arrayOfLabels) => array.map(({
                key,
                ...rest
            }) => ({
                ...rest,
                key,
                label: arrayOfLabels.find(({ key: keyToFind }) => keyToFind === key).label
            }));
            const { online = {}, presential = {}, totals = {} } = payload;
            const { absent, cancelled, pending, finalized } = totals;
            return {
                online: addLabelToArray(objectToArray(online), counters),
                presential: addLabelToArray(objectToArray(presential), counters),
                totals: {
                    absent: addLabelToArray(objectToArray(absent), profiles),
                    cancelled: addLabelToArray(objectToArray(cancelled), profiles),
                    finalized: addLabelToArray(objectToArray(finalized), profiles),
                    pending: addLabelToArray(objectToArray(pending), profiles)
                }
            };
        };
        yield put(statisticsActions.setStatisticsTurns(parseStatisticsTurns(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

function* _getStatisticsTurnsDownload ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsTurnsDownloading(true));
        const { data } = yield call(statisticsServices.fetchAppointmentListing, null, payload);
        yield put(statisticsActions.setStatisticsTurnsDownload(parseDownloadTurns(data)));
    } catch (e) {

    } finally {
        yield put(statisticsActions.setStatisticsTurnsDownloading(false));
    }
}

function* _getStatisticsVideocall ({ payload }) {
    try {
        yield put(statisticsActions.setStatisticsLoading(true));
        const { data } = yield call(statisticsServices.fetchVideocallData, null, payload);
        const parseStatisticsVideocall = list => ({
            ...list,
            diagnoses: orderBy(list.diagnoses, 'percentage', 'desc'),
            recommendations: orderBy(list.recommendations, 'percentage', 'desc'),
            symptom: orderBy(list.symptom, 'percentage', 'desc')
        });
        yield put(statisticsActions.setStatisticsVideocall(parseStatisticsVideocall(data)));
    } catch (e) {
        
    } finally {
        yield put(statisticsActions.setStatisticsLoading(false));
    }
}

export default [
    takeLatest(statisticsActions.getStatisticsAbsenceRate.type, _getStatisticsAbsenceRate),
    takeLatest(statisticsActions.getStatisticsAbsenceReports.type, _getStatisticsAbsenceReports),
    takeLatest(statisticsActions.getStatisticsAbsenceReportsDownload.type, _getStatisticsAbsenceReportsDownload),
    takeLatest(statisticsActions.getStatisticsAbsenceReportsPage.type, _getStatisticsAbsenceReportsPage),
    takeLatest(statisticsActions.getStatisticsAbsencesActive.type, _getStatisticsAbsencesActive),
    takeLatest(statisticsActions.getStatisticsAbsencesByLicense.type, _getStatisticsAbsencesByLicense),
    takeLatest(statisticsActions.getStatisticsAbsencesByLicenseMonthly.type, _getStatisticsAbsencesByLicenseMonthly),
    takeLatest(statisticsActions.getStatisticsDiagnosis.type, _getStatisticsDiagnosis),
    takeLatest(statisticsActions.getStatisticsDiagnosisGrouped.type, _getStatisticsDiagnosisGrouped),
    takeLatest(statisticsActions.getStatisticsGeneral.type, _getStatisticsGeneral),
    takeLatest(statisticsActions.getStatisticsHealthSurveys.type, _getStatisticsHealthSurveys),
    takeLatest(statisticsActions.getStatisticsHealthSurveysDownload.type, _getStatisticsHealthSurveysDownload),
    takeLatest(statisticsActions.getStatisticsLicenses.type, _getStatisticsLicenses),
    takeLatest(statisticsActions.getStatisticsLicensesActive.type, _getStatisticsLicensesActive),
    takeLatest(statisticsActions.getStatisticsLicensesDownload.type, _getStatisticsLicensesDownload),
    takeLatest(statisticsActions.getStatisticsLicensesGranted.type, _getStatisticsLicensesGranted),
    takeLatest(statisticsActions.getStatisticsLicensesHealthIndex.type, _getStatisticsLicensesHealthIndex),
    takeLatest(statisticsActions.getStatisticsPatientsAttended.type, _getStatisticsPatientsAttended),
    takeLatest(statisticsActions.getStatisticsPatientsAttendedDownload.type, _getStatisticsPatientsAttendedDownload),
    takeLatest(statisticsActions.getStatisticsPreworks.type, _getStatisticsPreworks),
    takeLatest(statisticsActions.getStatisticsPreworksDownload.type, _getStatisticsPreworksDownload),
    takeLatest(statisticsActions.getStatisticsProfessionals.type, _getStatisticsProfessionals),
    takeLatest(statisticsActions.getStatisticsProfessionalsDownload.type, _getStatisticsProfessionalsDownload),
    takeLatest(statisticsActions.getStatisticsPsychosocialRisk.type, _getStatisticsPsychosocialRisk),
    takeLatest(statisticsActions.getStatisticsPsychosocialRiskDownload.type, _getStatisticsPsychosocialRiskDownload),
    takeLatest(statisticsActions.getStatisticsQuizzes.type, _getStatisticsQuizzes),
    takeLatest(statisticsActions.getStatisticsQuizzesDownload.type, _getStatisticsQuizzesDownload),
    takeLatest(statisticsActions.getStatisticsRecipeRequests.type, _getStatisticsRecipeRequests),
    takeLatest(statisticsActions.getStatisticsRecipeRequestsDownload.type, _getStatisticsRecipeRequestsDownload),
    takeLatest(statisticsActions.getStatisticsRecommendations.type, _getStatisticsRecommendations),
    takeLatest(statisticsActions.getStatisticsRiskFactors.type, _getStatisticsRiskFactors),
    takeLatest(statisticsActions.getStatisticsRiskFactorsDownload.type, _getStatisticsRiskFactorsDownload),
    takeLatest(statisticsActions.getStatisticsRiskFactorsPage.type, _getStatisticsRiskFactorsPage),
    takeLatest(statisticsActions.getStatisticsSessions.type, _getStatisticsSessions),
    takeLatest(statisticsActions.getStatisticsSessionsDownload.type, _getStatisticsSessionsDownload),
    takeLatest(statisticsActions.getStatisticsSurveysDownload.type, _getStatisticsSurveysDownload),
    takeLatest(statisticsActions.getStatisticsSurveys.type, _getStatisticsSurveys),
    takeLatest(statisticsActions.getStatisticsSymptoms.type, _getStatisticsSymptoms),
    takeLatest(statisticsActions.getStatisticsTurns.type, _getStatisticsTurns),
    takeLatest(statisticsActions.getStatisticsTurnsDownload.type, _getStatisticsTurnsDownload),
    takeLatest(statisticsActions.getStatisticsVideocall.type, _getStatisticsVideocall)
];
